'use client';

import { useFormatter, useTranslations } from 'next-intl';
import type { FuturesPair } from '@/lib/api/futures/client';
import { TokenIcon } from '@/lib/ui-kit/token-icon';
import {
  CHANGE_DIRECTION_TEXT,
  changeDirection,
  formatPairChange,
  formatPairPrice,
} from '@/lib/utils/pairFormat';
import { useSearch } from '../core/SearchContext';
import { Highlight } from '../shared/Highlight';
import { RowFigures, RowText, SearchRow } from '../shared/SearchRow';

const PAIR_ICON_SIZE = 24;

interface PairRowProps {
  pair: FuturesPair;
}

/** A futures pair: symbol, 🔥 when trending, its last price and the day's change. */
export function PairRow({ pair }: PairRowProps) {
  const t = useTranslations('search');
  const format = useFormatter();
  const { select } = useSearch();

  const handleSelect = () =>
    select({ id: `crypto:${pair.id}`, kind: 'crypto', label: pair.symbol, href: pair.path });

  const trending = pair.trending ? (
    <span aria-hidden className="shrink-0 text-[11px]">
      🔥
    </span>
  ) : null;

  return (
    <SearchRow href={pair.path} onSelect={handleSelect}>
      <TokenIcon
        symbol={pair.baseCurrency}
        iconUrl={pair.icon}
        size={PAIR_ICON_SIZE}
        fallback="initials"
        className="shrink-0 rounded-full"
      />
      <RowText title={<Highlight text={pair.symbol} />} badge={trending} meta={t('perpetual')} />
      <RowFigures
        value={formatPairPrice(format, pair)}
        detail={formatPairChange(format, pair)}
        detailClassName={CHANGE_DIRECTION_TEXT[changeDirection(pair.changePct)]}
      />
    </SearchRow>
  );
}
